import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ScormPackageModel } from 'src/models/scormPackageModel';
import { DiffusionPilotageModel } from 'src/models/pilotage/diffusionPilotageModel';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ajouter-modifier-diffusion-dialog',
    templateUrl: './ajouter-modifier-diffusion-dialog.component.html',
    styleUrls: ['./ajouter-modifier-diffusion-dialog.component.scss'],
    imports: [
        MatDialogModule,
        TranslateModule,
        MatProgressSpinnerModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatOptionModule,
        NgxMatSelectSearchModule
    ]
})
export class AjouterModifierDiffusionDialogComponent implements OnInit, OnDestroy {
  scormPackages: ScormPackageModel[] = [];
  scormPackageSelectionne: number | null = null;
  langueSelectionnee: string | null = null;
  isLoading: boolean = false;
  currentLang: string = 'fr';

  scormPackageFilterCtrl = new FormControl();
  filteredScormPackages = new ReplaySubject<ScormPackageModel[]>(1);
  private _onDestroy = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<AjouterModifierDiffusionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { mode: 'ajouter' | 'modifier'; diffusion?: DiffusionPilotageModel; scormPackages: ScormPackageModel[] },
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.scormPackages = (this.data.scormPackages || []).sort((a, b) =>
      this.getPackageTitle(a).localeCompare(this.getPackageTitle(b))
    );
    this.currentLang = this.translate.currentLang || 'fr';

    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
      this.filterScormPackages();
    });

    this.setupFilterSubscriptions();
    this.filterScormPackages();

    if (this.data.mode === 'modifier' && this.data.diffusion) {
      this.scormPackageSelectionne = this.data.diffusion.scormPackageId || null;
      this.langueSelectionnee = this.data.diffusion.langue || null;
    }
  }

  private setupFilterSubscriptions(): void {
    this.scormPackageFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterScormPackages();
      });
  }

  private filterScormPackages(): void {
    if (!this.scormPackages) {
      return;
    }
    let search = this.scormPackageFilterCtrl.value;
    if (!search) {
      this.filteredScormPackages.next(this.scormPackages.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredScormPackages.next(
      this.scormPackages.filter(pkg =>
        this.getPackageTitle(pkg).toLowerCase().indexOf(search) > -1
      )
    );
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.scormPackageSelectionne && this.langueSelectionnee) {
      this.dialogRef.close({
        scormPackageId: this.scormPackageSelectionne,
        langue: this.langueSelectionnee
      });
    }
  }

  getPackageTitle(scormPackage: ScormPackageModel): string {
    const locale = scormPackage.locales?.find(l => l.langue === this.currentLang)
      || scormPackage.locales?.find(l => l.langue === 'fr')
      || scormPackage.locales?.[0];
    return locale?.titre || scormPackage.nomPackage || '';
  }
}
